import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, ACCOUNT_TYPES } from '../context/AuthContext'
import { Warehouse, Building2, ChevronDown, Plus, Check } from 'lucide-react'

const ACCOUNT_TYPE_ICONS = {
  [ACCOUNT_TYPES.WAREHOUSE]: Warehouse,
  [ACCOUNT_TYPES.CLIENT]: Building2,
}

const ACCOUNT_TYPE_LABELS = {
  [ACCOUNT_TYPES.WAREHOUSE]: 'Склад',
  [ACCOUNT_TYPES.CLIENT]: 'Клиент',
}

const ACCOUNT_TYPE_COLORS = {
  [ACCOUNT_TYPES.WAREHOUSE]: 'bg-blue-50 border-blue-300 text-blue-800',
  [ACCOUNT_TYPES.CLIENT]: 'bg-emerald-50 border-emerald-300 text-emerald-800',
}

const ACCOUNT_TYPE_ICON_COLORS = {
  [ACCOUNT_TYPES.WAREHOUSE]: 'text-blue-600',
  [ACCOUNT_TYPES.CLIENT]: 'text-emerald-600',
}

const ACCOUNT_TYPE_BADGE_COLORS = {
  [ACCOUNT_TYPES.WAREHOUSE]: 'bg-blue-100 text-blue-700',
  [ACCOUNT_TYPES.CLIENT]: 'bg-emerald-100 text-emerald-700',
}

export default function AccountSwitcher() {
  const { accounts, activeAccountId, switchAccount } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const activeAccount = accounts.find(a => a.account_id === activeAccountId)
  if (!activeAccount) return null

  const ActiveIcon = ACCOUNT_TYPE_ICONS[activeAccount.account_type] || Warehouse
  const otherAccounts = accounts.filter(a => a.account_id !== activeAccountId)

  const handleSwitch = async (accountId) => {
    setSwitching(true)
    setIsOpen(false)
    await switchAccount(accountId)
    // Page will reload after switchAccount
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium border transition-colors ${ACCOUNT_TYPE_COLORS[activeAccount.account_type] || 'bg-gray-50 border-gray-300 text-gray-800'} hover:opacity-80`}
      >
        <ActiveIcon className={`h-4 w-4 ${ACCOUNT_TYPE_ICON_COLORS[activeAccount.account_type] || 'text-gray-600'}`} />
        <span className="max-w-[140px] truncate">{activeAccount.account_name}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${ACCOUNT_TYPE_BADGE_COLORS[activeAccount.account_type] || 'bg-gray-100 text-gray-600'}`}>
          {ACCOUNT_TYPE_LABELS[activeAccount.account_type]}
        </span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {/* Current account */}
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="text-xs text-gray-400 mb-1">Текущий аккаунт</div>
            <div className="flex items-center space-x-2">
              <ActiveIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-900 truncate">{activeAccount.account_name}</span>
              <span className="text-xs text-gray-400">{ACCOUNT_TYPE_LABELS[activeAccount.account_type]}</span>
              <Check className="h-4 w-4 text-blue-600 ml-auto flex-shrink-0" />
            </div>
          </div>

          {/* Other accounts */}
          {otherAccounts.map((account) => {
            const Icon = ACCOUNT_TYPE_ICONS[account.account_type] || Warehouse
            return (
              <button
                key={account.account_id}
                onClick={() => handleSwitch(account.account_id)}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Icon className="h-4 w-4 text-gray-400" />
                <span className="truncate">{account.account_name}</span>
                <span className="text-xs text-gray-400">{ACCOUNT_TYPE_LABELS[account.account_type]}</span>
              </button>
            )
          })}

          {/* Create new account */}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/accounts/new')
              }}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
            >
              <Plus className="h-4 w-4" />
              <span>Создать аккаунт</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
